/**
 * Phase 24.4b Step 6 — day header with date + macros + fit_status traffic light.
 *
 * fit_status is computed server-side; we render the traffic-light
 * directly from `day.fit_status` (no client recomputation here — that
 * happens only in optimistic-update payloads via computeFitStatus).
 */
import { useTranslation } from "react-i18next";
import { ChevronDown, ChevronRight } from "lucide-react";
import type { DayTotals, FitStatus } from "@/api/meal_plan";
import { fmtDate } from "@/lib/dates";
import { cn } from "@/lib/utils";

const FIT_STATUS_TONE: Record<FitStatus, { dot: string; label: string }> = {
  ok: { dot: "bg-amber", label: "meal_plan.fit_ok" },
  warn: { dot: "bg-amber-dark", label: "meal_plan.fit_warn" },
  fail: { dot: "bg-coral", label: "meal_plan.fit_fail" },
};

interface Props {
  day: DayTotals;
  expanded: boolean;
  onToggle?: () => void;
}

export function DayHeader({ day, expanded, onToggle }: Props) {
  const { t } = useTranslation();
  const tone = FIT_STATUS_TONE[day.fit_status];
  const isToday = day.date === new Date().toISOString().slice(0, 10);
  const target = day.target_kcal ?? 0;
  const planned = Math.round(day.planned_kcal);

  return (
    <button
      type="button"
      onClick={onToggle}
      disabled={!onToggle}
      className={cn(
        "flex w-full items-center justify-between gap-3 rounded-lg border border-cream-border bg-cream-light p-3 text-left transition",
        onToggle ? "hover:bg-cream-border/30" : "cursor-default",
        isToday ? "ring-1 ring-amber/40" : "",
      )}
      data-testid={`day-header-${day.date}`}
      aria-expanded={onToggle ? expanded : undefined}
    >
      <div className="flex min-w-0 flex-1 items-center gap-3">
        <span
          className={cn("h-2.5 w-2.5 shrink-0 rounded-full", tone.dot)}
          aria-label={t(tone.label)}
          title={t(tone.label)}
        />
        <div className="min-w-0">
          <div className="text-[11px] uppercase tracking-wider text-taupe">
            {fmtDate(day.date, "EEE")}
          </div>
          <div className="text-[14px] font-medium text-navy-deep">
            {fmtDate(day.date, "d MMM")}
          </div>
        </div>
        <div className="ml-auto text-right text-[11px] text-taupe">
          <div className="metric-number text-[14px] text-navy-deep">
            {planned}
            <span className="text-[11px] text-taupe">
              {target ? ` / ${Math.round(target)}` : ""} {t("nutrition_page.kcal")}
            </span>
          </div>
          <div className="text-[10px] text-taupe">{day.day_profile}</div>
        </div>
      </div>
      {onToggle ? (
        expanded ? (
          <ChevronDown className="h-4 w-4 shrink-0 text-taupe" strokeWidth={2} />
        ) : (
          <ChevronRight className="h-4 w-4 shrink-0 text-taupe" strokeWidth={2} />
        )
      ) : null}
    </button>
  );
}
